import * as yup from 'yup';

import Regex from 'constants/regex';
import { DATE_FORMATS } from 'utilities/constants';
import dayjs from 'dayjs';
import { isDateBetween } from 'utilities/dayjs-utilities';
import Validation from 'constants/validation-messages';
import { CardModalFormNames } from './CardModalFormNames';

const minExpiryMonths = 1;
const maxExpiryMonths = 60;

const addressValidationSchema = yup.object().shape({
  [CardModalFormNames.ContactFirstName]: yup
    .string()
    .nullable()
    .transform((value) => (value ? value.trim() : value))
    .matches(Regex.ALPHA_NUM_SPACE_SPECIAL_CHARACTER, Validation.ALPHA_NUM_SPECIAL_CHARACTER)
    .max(19, Validation.MAX_CHAR_NINETEEN),
  [CardModalFormNames.ContactLastName]: yup
    .string()
    .nullable()
    .transform((value) => (value ? value.trim() : value))
    .matches(Regex.ALPHA_NUM_SPACE_SPECIAL_CHARACTER, Validation.ALPHA_NUM_SPECIAL_CHARACTER)
    .max(19, Validation.MAX_CHAR_NINETEEN),
  [CardModalFormNames.AddressLineOne]: yup
    .string()
    .nullable()
    .transform((value) => (value ? value.trim() : value))
    .max(40, Validation.MAX_CHAR_FORTY)
    .optional(),
  [CardModalFormNames.AddressLineTwo]: yup
    .string()
    .nullable()
    .transform((value) => (value ? value.trim() : value))
    .required(Validation.ADDRESS_LINE_2_REQUIRED)
    .max(40, Validation.MAX_CHAR_FORTY),
  [CardModalFormNames.City]: yup
    .string()
    .nullable()
    .transform((value) => (value ? value.trim() : value))
    .required(Validation.CITY_REQUIRED)
    .max(30, Validation.MAX_CHAR_THIRTY),
  [CardModalFormNames.State]: yup.string().nullable().required(Validation.STATE_REQUIRED),
  [CardModalFormNames.Postcode]: yup
    .string()
    .nullable()
    .required(Validation.POSTCODE_REQUIRED)
    .matches(Regex.POSTCODE, {
      excludeEmptyString: true,
      message: Validation.POSTCODE,
    }),
  [CardModalFormNames.RenewalOption]: yup.string().nullable().required(Validation.RENEWAL_REQUIRED),
});

const VASValidationSchema = yup.object().shape({
  [CardModalFormNames.VehicleRegistration]: yup
    .string()
    .nullable()
    .transform((value) => (value ? value.trim() : value))
    .matches(Regex.ALPHA_NUM_SPACE, Validation.ALPHA_NUM_SPACE)
    .max(12, Validation.MAX_CHAR_TWELVE)
    .optional()
    .when(['roadsideAssist', 'serviceAndRepairs'], {
      is: (roadsideAssist, serviceAndRepairs) => roadsideAssist || serviceAndRepairs,
      then: yup
        .string()
        .nullable()
        .transform((value) => (value ? value.trim() : value))
        .matches(Regex.ALPHA_NUM_SPACE, Validation.ALPHA_NUM_SPACE)
        .max(12, Validation.MAX_CHAR_TWELVE)
        .required(Validation.REGISTRATION_NUMBER_REQUIRED),
    }),
  [CardModalFormNames.RegistrationState]: yup
    .string()
    .nullable()
    .optional()
    .when(['roadsideAssist', 'serviceAndRepairs'], {
      is: (roadsideAssist, serviceAndRepairs) => roadsideAssist || serviceAndRepairs,
      then: yup.string().nullable().required(Validation.REGISTRATION_STATE_REQUIRED),
    }),
  [CardModalFormNames.VehicleDescription]: yup
    .string()
    .nullable()
    .transform((value) => (value ? value.trim() : value))
    .max(50, Validation.MAX_CHAR_FIFTY)
    .optional()
    .when(['roadsideAssist', 'serviceAndRepairs'], {
      is: (roadsideAssist, serviceAndRepairs) => roadsideAssist || serviceAndRepairs,
      then: yup
        .string()
        .nullable()
        .transform((value) => (value ? value.trim() : value))
        .max(50, Validation.MAX_CHAR_FIFTY)
        .required(Validation.VEHICLE_DESCRIPTION_REQUIRED),
    }),
});

export const cardDetailsValidationSchema = (isEdit: boolean, disableExpiry: boolean = true) => {
  return yup.object().shape({
    [CardModalFormNames.EmbossingName]: yup
      .string()
      .nullable()
      .transform((value) => (value ? value.trim() : value))
      .required(Validation.EMBOSSING_NAME_REQUIRED)
      .matches(Regex.ALPHA_NUM_SPACE_SPECIAL_CHARACTER, Validation.ALPHA_NUM_SPECIAL_CHARACTER)
      .max(27, Validation.MAX_CHAR_TWENTY_SEVEN),
    [CardModalFormNames.EmbossingLine]: yup
      .string()
      .transform((value) => (value ? value.trim() : value))
      .nullable()
      .matches(Regex.ALPHA_NUM_SPACE_SPECIAL2, Validation.ALPHA_NUM_SPACE_SPECIAL2)
      .max(27, Validation.MAX_CHAR_TWENTY_SEVEN),
    [CardModalFormNames.IssueDateTo]: yup
      .string()
      .required(Validation.EXPIRY_DATE_REQUIRED)
      .matches(Regex.EXPIRY_MONTH_YEAR, Validation.EXPIRY_DATE_MONTH_YEAR)
      .test(
        'expiryDate',
        `Must be between ${dayjs()
          .tz('Australia/Sydney')
          .add(minExpiryMonths + 1, 'month')
          .endOf('month')
          .format(
            'MM/YY'
          )} to ${dayjs().tz('Australia/Sydney').add(maxExpiryMonths, 'months').startOf('month').format('MM/YY')}`,
        (value) => {
          if (!value || !value.match(Regex.EXPIRY_MONTH_YEAR)) {
            return true;
          }
          if (isEdit && disableExpiry) {
            return true;
          }
          const inputDate = dayjs(`01/${value}`, DATE_FORMATS).tz('Australia/Sydney');

          const minDate = dayjs().add(minExpiryMonths, 'month').endOf('month').tz('Australia/Sydney');
          const maxDate = dayjs()
            .add(maxExpiryMonths + 1, 'months')
            .startOf('month')
            .tz('Australia/Sydney');

          return isDateBetween(inputDate, minDate, maxDate);
        }
      ),
    [CardModalFormNames.CostCentreCode]: yup
      .string()
      .nullable()
      .transform((value) => (value ? value.trim() : value))
      .matches(Regex.ALPHA_NUM_SPACE_HYPHEN, Validation.ALPHA_NUM_SPACE_HYPHEN)
      .max(36, Validation.MAX_CHAR_THIRTY_SIX),
    [CardModalFormNames.DriverName]: yup
      .string()
      .nullable()
      .transform((value) => (value ? value.trim() : value))
      .matches(Regex.DRIVER_NAME, Validation.DRIVER_NAME)
      .max(27, Validation.MAX_CHAR_TWENTY_SEVEN),
    [CardModalFormNames.DomesticNumber]: yup
      .string()
      .nullable()
      .transform((value) => (value ? value.trim() : value))
      .matches(Regex.ALPHA_NUM_SPACE, Validation.ALPHA_NUM_SPACE)
      .max(16, Validation.MAX_CHAR_SIXTEEN),
  });
};

export const getCardUsageOptionsSchema = (fleetLimiterCodes: string[], favouriteNamesArray: string[] = []) => {
  return yup.object().shape({
    // Card Usage Options Validation
    cardFavoriteName: yup
      .string()
      .required(Validation.CARD_FAV_NAME_REQUIRED)
      .matches(Regex.ALPHA_NUM_SPACE, Validation.ALPHA_NUM_SPACE)
      .test('is-unique', Validation.CARD_FAV_NAME_UNIQUE, function (val) {
        return !favouriteNamesArray.includes(val?.toUpperCase());
      }),
    [CardModalFormNames.RoadsideAssistance]: yup.boolean(),
    [CardModalFormNames.VehicleType]: yup
      .string()
      .nullable()
      .optional()
      .when(CardModalFormNames.RoadsideAssistance, {
        is: true,
        then: yup.string().nullable().required(Validation.VEHICLE_TYPE_REQUIRED),
      }),
    [CardModalFormNames.RoadsideTerms]: yup
      .boolean()
      .optional()
      .when(CardModalFormNames.RoadsideAssistance, {
        is: true,
        then: yup
          .boolean()
          .required(Validation.TERMS_AND_CONDITIONS_REQUIRED)
          .isTrue(Validation.TERMS_AND_CONDITIONS_REQUIRED),
      }),
    // Products and Services Validation
    [CardModalFormNames.AllProducts]: yup
      .boolean()
      .when(fleetLimiterCodes ? fleetLimiterCodes.filter((code) => code) : [], {
        is: (...theArgs) => !theArgs.some((val) => val == true),
        then: yup.boolean().required(Validation.FLEET_LIMITERS_REQUIRED).isTrue(Validation.FLEET_LIMITERS_REQUIRED),
      }),
  });
};

const cardModalValidationSchema = yup
  .object()
  .shape({
    [CardModalFormNames.Account]: yup.string().nullable().required('Please select an account number.'),
  })
  .concat(addressValidationSchema)
  .concat(VASValidationSchema);

export default cardModalValidationSchema;
